<script setup lang="ts">
import axios from 'axios';
import { ref } from 'vue';
import ToolPageHead from '@/components/seo/ToolPageHead.vue';
import DropZone from '@/components/Tools/DropZone.vue';
import FileCard from '@/components/Tools/FileCard.vue';
import StatusMessage from '@/components/Tools/StatusMessage.vue';
import ToolLayout from '@/components/Tools/ToolLayout.vue';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { usePdfUploadLimit } from '@/composables/usePdfUploadLimit';
import { useToolPageSeoFromInertia } from '@/composables/useToolPageSeo';
import { useTranslations } from '@/composables/useTranslations';
import { dashboard } from '@/routes';
import { ocr as ocrPage } from '@/routes/tools/pdf';
import ocrTool from '@/routes/tools/pdf/ocr';

const { lineWithMax } = usePdfUploadLimit();
const { t } = useTranslations();

const seo = useToolPageSeoFromInertia({
    seoPrefix: 'tools.pdf.ocr.seo',
    fallbackTitleKey: 'tools.pdf.ocr.title',
    fallbackTitleLabel: 'OCR PDF',
    t,
    homeRelativeUrl: dashboard.url(),
    selfRelativeUrl: ocrPage.url(),
});

defineOptions({
    layout: {
        breadcrumbs: [
            {
                translationKey: 'tools.nav.home',
                title: 'Home',
                href: dashboard.url(),
            },
            {
                translationKey: 'tools.pdf.ocr.title',
                title: 'OCR PDF',
                href: ocrPage.url(),
            },
        ],
    },
});

type OcrLanguage = 'eng' | 'srp' | 'deu' | 'fra' | 'spa';
type Status = 'idle' | 'loading' | 'success' | 'error';

const file = ref<File | null>(null);
const language = ref<OcrLanguage>('eng');
const deskew = ref(true);
const skipText = ref(true);
const status = ref<Status>('idle');
const statusMessage = ref('');

const languageOptions: {
    value: OcrLanguage;
    labelKey: string;
    fallback: string;
}[] = [
    {
        value: 'eng',
        labelKey: 'tools.pdf.ui.ocr_lang_eng',
        fallback: 'English',
    },
    {
        value: 'srp',
        labelKey: 'tools.pdf.ui.ocr_lang_srp',
        fallback: 'Serbian',
    },
    { value: 'deu', labelKey: 'tools.pdf.ui.ocr_lang_deu', fallback: 'German' },
    { value: 'fra', labelKey: 'tools.pdf.ui.ocr_lang_fra', fallback: 'French' },
    {
        value: 'spa',
        labelKey: 'tools.pdf.ui.ocr_lang_spa',
        fallback: 'Spanish',
    },
];

function onFileSelected(files: File[]): void {
    file.value = files[0] ?? null;
    status.value = 'idle';
    statusMessage.value = '';
}

function removeFile(): void {
    file.value = null;
    status.value = 'idle';
    statusMessage.value = '';
}

async function extractError(err: unknown): Promise<string> {
    const e = err as { response?: { data: Blob | object } };

    if (e.response?.data instanceof Blob) {
        const text = await (e.response.data as Blob).text();

        try {
            const parsed = JSON.parse(text) as {
                errors?: Record<string, string[]>;
            };
            const messages = Object.values(parsed.errors ?? {}).flat();
            const msg =
                (messages[0] as string) ||
                t('tools.pdf.ui.ocr_failed', 'OCR failed.');

            if (
                /ocrmypdf|tesseract/i.test(msg) ||
                msg === t('tools.ocrmypdf.unavailable_json', '')
            ) {
                return t(
                    'tools.pdf.ui.ocr_unavailable',
                    'OCR is not available on this server. OCRmyPDF and Tesseract must be installed.',
                );
            }

            return msg;
        } catch {
            return t('tools.pdf.ui.ocr_failed', 'OCR failed.');
        }
    }

    return t('tools.pdf.ui.ocr_failed', 'OCR failed.');
}

async function runOcr(): Promise<void> {
    if (!file.value || status.value === 'loading') {
        return;
    }

    status.value = 'loading';
    statusMessage.value = t(
        'tools.pdf.ui.ocr_processing',
        'Running OCR… This may take a few minutes for large scans.',
    );

    const formData = new FormData();
    formData.append('file', file.value);
    formData.append('language', language.value);
    formData.append('deskew', deskew.value ? '1' : '0');
    formData.append('skip_text', skipText.value ? '1' : '0');

    try {
        const response = await axios.post(ocrTool.submit.url(), formData, {
            responseType: 'blob',
        });

        const cd = (response.headers['content-disposition'] as string) ?? '';
        const match = cd.match(/filename="?([^"]+)"?/);
        const filename = match?.[1] ?? 'searchable.pdf';

        const blobUrl = URL.createObjectURL(response.data as Blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(blobUrl);

        status.value = 'success';
        statusMessage.value = t(
            'tools.pdf.ui.ocr_success',
            'Searchable PDF ready — check your downloads folder.',
        );
        file.value = null;
    } catch (err: unknown) {
        status.value = 'error';
        statusMessage.value = await extractError(err);
    }
}
</script>

<template>
    <ToolPageHead v-bind="seo" json-ld-head-key="ld-json-pdf-ocr" />

    <ToolLayout
        :title="t('tools.pdf.ocr.title', 'OCR PDF')"
        :subtitle="t('tools.pdf.ocr.description', '')"
    >
        <div class="space-y-4">
            <div class="rounded-xl border border-border bg-card p-6 shadow-sm">
                <DropZone
                    accept="application/pdf"
                    :multiple="false"
                    :label="
                        t(
                            'tools.pdf.ui.drop_pdf_here',
                            'Click or drag PDF here',
                        )
                    "
                    :sublabel="
                        lineWithMax(
                            t('tools.pdf.ui.pdf_files_only', 'PDF files only'),
                        )
                    "
                    @update:model-value="onFileSelected"
                />

                <div v-if="file" class="mt-4 space-y-3">
                    <FileCard
                        :file="file"
                        :show-drag-handle="false"
                        @remove="removeFile"
                    />
                    <p class="text-xs leading-relaxed text-muted-foreground">
                        {{
                            t(
                                'tools.pdf.ui.ocr_wait_hint',
                                'Large scanned PDFs can take several minutes. Keep this tab open until the download starts.',
                            )
                        }}
                    </p>
                </div>
            </div>

            <div class="rounded-xl border border-border bg-card p-6 shadow-sm">
                <p class="mb-2 text-sm font-medium">
                    {{
                        t(
                            'tools.pdf.ui.ocr_language_label',
                            'Document language',
                        )
                    }}
                </p>
                <select
                    v-model="language"
                    class="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                    <option
                        v-for="opt in languageOptions"
                        :key="opt.value"
                        :value="opt.value"
                    >
                        {{ t(opt.labelKey, opt.fallback) }}
                    </option>
                </select>

                <div class="mt-4 space-y-3">
                    <div class="flex items-start gap-2">
                        <Checkbox
                            id="ocr-deskew"
                            v-model="deskew"
                            class="mt-0.5 shrink-0"
                        />
                        <Label for="ocr-deskew" class="cursor-pointer text-sm">
                            {{
                                t(
                                    'tools.pdf.ui.ocr_deskew',
                                    'Deskew crooked scans',
                                )
                            }}
                        </Label>
                    </div>
                    <div class="flex items-start gap-2">
                        <Checkbox
                            id="ocr-skip-text"
                            v-model="skipText"
                            class="mt-0.5 shrink-0"
                        />
                        <Label
                            for="ocr-skip-text"
                            class="cursor-pointer text-sm"
                        >
                            {{
                                t(
                                    'tools.pdf.ui.ocr_skip_text',
                                    'Skip pages that already have text',
                                )
                            }}
                        </Label>
                    </div>
                </div>
            </div>

            <Button
                class="w-full bg-brand-600 text-white hover:bg-brand-700"
                :disabled="!file || status === 'loading'"
                @click="runOcr"
            >
                {{ t('tools.pdf.ui.ocr_button', 'Make PDF searchable') }}
            </Button>

            <StatusMessage :status="status" :message="statusMessage" />
        </div>
    </ToolLayout>
</template>
